// Explore-stats page: Members in both chambers (#218).
// Parliamentarians who served in BOTH the House and the Senate, derived
// from `recordsofservice` ROSTypeID=9 (Parliamentary Service) with the
// `MpOrSenator` tag distinguishing chamber. Direction (Reps→Senate vs
// Senate→Reps) is derived from the earliest tenure per chamber; gap is
// the time between leaving one chamber and joining the other.

import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Grid, Box, Card, CardContent,
} from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, PALETTE } from './charts';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
} from 'recharts';

function StatCard({ label, value, sub }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 3 }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ mt: 0.5, lineHeight: 1.1 }}>
          {value ?? '—'}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function BothChambers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('both-chambers');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const c = data?.charts || {};
  const dir = c.direction_split || { member_first: 0, senator_first: 0 };
  const cov = c.coverage || { total: 0 };

  const dirPie = [
    { name: 'Reps → Senate', value: dir.member_first },
    { name: 'Senate → Reps', value: dir.senator_first },
  ];

  return (
    <ExploreStatsPage
      title="Members in both chambers"
      description="Parliamentarians who served in both the House of Representatives and the Senate. Derived from `recordsofservice` ROSTypeID=9 (Parliamentary Service) with the `MpOrSenator` tag distinguishing chamber per tenure. Direction is determined by the earliest tenure per chamber; gap is the time between leaving one chamber and joining the other (negative gaps mean overlapping service, which can happen across the appointment/election boundary)."
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="both-chambers"
    >
      {data && (
        <Box>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={3}>
              <StatCard label="Total dual-chamber members"
                        value={cov.total} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Reps → Senate"
                        value={dir.member_first} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Senate → Reps"
                        value={dir.senator_first} />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatCard label="Reps→Senate share"
                        value={cov.total
                          ? `${Math.round(dir.member_first * 100 / cov.total)}%`
                          : '—'} />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Direction of transition
                </Typography>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={dirPie} dataKey="value" nameKey="name"
                         innerRadius={60} outerRadius={100} label>
                      <Cell fill={PALETTE[1]} />
                      <Cell fill={PALETTE[0]} />
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Gap between chambers
                </Typography>
                <StatBar data={c.gap_distribution || []} xKey="bucket"
                         yKey="count" height={280} />
                <Typography variant="caption" color="text.secondary"
                            sx={{ display: 'block', mt: 1 }}>
                  "Immediate / overlap" includes members whose two-chamber
                  service overlapped briefly across the resignation/swearing-in
                  boundary.
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Cross-chamber moves per decade (year of second-chamber start)
                </Typography>
                <StatBar data={c.per_decade || []} xKey="decade"
                         yKey="count" height={320} />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </ExploreStatsPage>
  );
}
